import React, { useState, useEffect, useRef, useMemo } from "react";
import { trackError } from "../../../../lib/metrics";

const SEGMENTS = ["a", "b", "c", "d", "e", "f", "g"];

const DIGIT_SEGMENTS = {
  0: ["a", "b", "c", "d", "e", "f"],
  1: ["b", "c"],
  2: ["a", "b", "g", "e", "d"],
  3: ["a", "b", "c", "d", "g"],
  4: ["f", "g", "b", "c"],
  5: ["a", "f", "g", "c", "d"],
  6: ["a", "f", "e", "d", "c", "g"],
  7: ["a", "b", "c"],
  8: ["a", "b", "c", "d", "e", "f", "g"],
  9: ["a", "b", "c", "d", "f", "g"],
};

function cx(...parts) { return parts.filter(Boolean).join(" "); }

function SevenSegDigit({ digit, missingSegments = [], size = 88, flickerBroken = true }) {
  const active = DIGIT_SEGMENTS[digit] || [];
  const isLit = (seg) => active.includes(seg) && !missingSegments.includes(seg);
  const isBroken = (seg) => active.includes(seg) && missingSegments.includes(seg);
  const isUnused = (seg) => !active.includes(seg);

  return (
    <div className="nbcp_digit" style={{ width: size * 0.62, height: size }} aria-label={`digit-${digit}`}>
      {SEGMENTS.map((seg) => (
        <div key={seg} className={cx("nbcp_seg", `nbcp_${seg}`,
          isLit(seg) && "nbcp_on",
          isBroken(seg) && (flickerBroken ? "nbcp_broken nbcp_flicker" : "nbcp_broken"),
          isUnused(seg) && "nbcp_unused"
        )} />
      ))}
    </div>
  );
}

function NeonKeypad({ onPress, disabled }) {
  const KEYS = useMemo(() => ["1", "2", "3", "4", "5", "6", "7", "8", "9", "←", "0", "✓"], []);
  return (
    <div className="nbcp_keypad" aria-label="neon-keypad">
      {KEYS.map((k) => (
        <button key={k} type="button" disabled={disabled} className="nbcp_key" onClick={() => onPress(k)}>{k}</button>
      ))}
    </div>
  );
}

export function NeonBrokenCodePad({
  solved = false,
  open,
  onClose,
  onSolved,
  title = "Boîtier — Afficheur instable",
  solution = "2132",
  flickerBroken = true,
  // trackError props
  sessionCode,
  stage,
  playerId,
}) {
  const [input, setInput] = useState("");
  const [status, setStatus] = useState("idle");
  const solvedOnceRef = useRef(false);

  useEffect(() => {
    if (!solved) return;
    setStatus("solved");
    setInput(solution);
    solvedOnceRef.current = true;
  }, [solved, solution]);

  useEffect(() => {
    if (!open) return;
    if (solved) return;
    setInput("");
    setStatus("idle");
    solvedOnceRef.current = false;
  }, [open, solved]);

  function press(k) {
    if (solved || status === "solved") return;
    if (k === "←") { setStatus("idle"); setInput((s) => s.slice(0, -1)); return; }
    if (k === "✓") {
      if (input.length !== 4) { setStatus("error"); return; }
      if (input === solution) {
        setStatus("solved");
        if (!solvedOnceRef.current) { solvedOnceRef.current = true; onSolved?.(); }
      } else {
        setStatus("error");
        // ── trackError ──
        trackError({
          sessionCode,
          stage,
          playerId,
          reason: "neon_broken_code_pad_wrong_code",
          meta: { entered: input },
        });
      }
      return;
    }
    setStatus("idle");
    setInput((s) => (s.length >= 4 ? s : s + k));
  }

  const broken = useMemo(() => [
    { d: 9, miss: ["c", "g"] },
    { d: 8, miss: ["g"] },
    { d: 8, miss: ["b", "e", "g"] },
    { d: 7, miss: ["b", "c"] },
  ], []);

  return (
    <div className="space-y-3">
      <style>{`
        .nbcp_wrap { display:flex;flex-direction:column;gap:16px; }
        .nbcp_panel { border-radius:18px;border:1px solid rgba(255,255,255,.10);background:radial-gradient(1200px 700px at 50% 0%,rgba(255,46,207,.15),rgba(0,0,0,.85));padding:16px;box-shadow:0 18px 60px rgba(0,0,0,.65); }
        .nbcp_header { display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-bottom:10px; }
        .nbcp_hint { font-size:12px;color:rgba(255,255,255,.72);letter-spacing:.08em;text-transform:uppercase; }
        .nbcp_displayRow { position:relative;display:flex;align-items:center;justify-content:center;gap:14px;padding:14px 10px 34px;border-radius:16px;background:rgba(0,0,0,.55);border:1px solid rgba(255,255,255,.08);box-shadow:inset 0 0 0 1px rgba(255,46,207,.06),inset 0 0 22px rgba(255,46,207,.12);overflow:hidden; }
        .nbcp_screenLine { position:absolute;left:12px;right:12px;bottom:8px;display:flex;align-items:center;justify-content:center;font-size:9px;font-style:italic;letter-spacing:.22em;text-transform:uppercase;color:rgba(255,215,248,.92);text-shadow:0 0 10px rgba(255,46,207,.22),0 0 22px rgba(255,46,207,.16),0 0 36px rgba(255,46,207,.10);opacity:.82;user-select:none;animation:nbcpScreenFlicker 3.8s infinite; }
        @keyframes nbcpScreenFlicker { 0%{opacity:.82;filter:brightness(1)} 7%{opacity:.72;filter:brightness(0.98)} 9%{opacity:.86;filter:brightness(1.02)} 33%{opacity:.62;filter:brightness(0.94)} 35%{opacity:.84;filter:brightness(1.03)} 70%{opacity:.90;filter:brightness(1.06)} 100%{opacity:.82;filter:brightness(1)} }
        .nbcp_displayRow::after { content:"";position:absolute;left:-10%;right:-10%;height:18px;top:12px;background:linear-gradient(180deg,rgba(255,46,207,.08),rgba(0,0,0,0));opacity:.10;transform:translateY(0);animation:nbcpScan 5.5s linear infinite;pointer-events:none; }
        @keyframes nbcpScan { 0%{transform:translateY(0);opacity:.06} 40%{opacity:.10} 100%{transform:translateY(58px);opacity:.04} }
        .nbcp_digit { position:relative; }
        .nbcp_seg { position:absolute;border-radius:999px;transition:filter .2s,opacity .2s,background .2s; }
        .nbcp_a,.nbcp_d,.nbcp_g { height:12%;left:14%;right:14%; }
        .nbcp_a { top:6%; } .nbcp_g { top:44%; } .nbcp_d { bottom:6%; }
        .nbcp_b,.nbcp_c,.nbcp_e,.nbcp_f { width:12%;top:14%;bottom:14%; }
        .nbcp_b { right:6%;top:12%;bottom:52%; } .nbcp_c { right:6%;top:52%;bottom:12%; }
        .nbcp_f { left:6%;top:12%;bottom:52%; } .nbcp_e { left:6%;top:52%;bottom:12%; }
        .nbcp_on { background:#ff2ecf;opacity:1;filter:drop-shadow(0 0 6px rgba(255,46,207,.85)) drop-shadow(0 0 14px rgba(255,46,207,.55)); }
        .nbcp_broken { background:rgba(160,160,160,.28);opacity:.65;filter:none; }
        .nbcp_unused { background:rgba(255,255,255,.06);opacity:.08; }
        .nbcp_flicker { animation:nbcpFlicker 1.8s infinite; }
        @keyframes nbcpFlicker { 0%{opacity:.65;filter:none} 6%{opacity:.25;filter:blur(.4px)} 10%{opacity:.70;filter:none} 26%{opacity:.18;filter:blur(.6px)} 28%{opacity:.62;filter:none} 58%{opacity:.30} 60%{opacity:.66} 100%{opacity:.60} }
        .nbcp_inputBox { margin-top:12px;display:flex;align-items:center;justify-content:center; }
        .nbcp_code { width:260px;display:grid;grid-template-columns:repeat(4,1fr);gap:10px;padding:10px 12px;border-radius:14px;background:rgba(0,0,0,.55);border:1px solid rgba(255,255,255,.10);box-shadow:inset 0 0 0 1px rgba(255,46,207,.06);font-family:ui-monospace,monospace;font-size:18px; }
        .nbcp_dot { height:34px;border-radius:10px;background:rgba(255,255,255,.08);display:flex;align-items:center;justify-content:center;letter-spacing:.1em;color:rgba(255,255,255,.9); }
        .nbcp_dotFilled { background:rgba(255,46,207,.18);box-shadow:0 0 10px rgba(255,46,207,.35);color:rgba(255,220,250,.98);text-shadow:0 0 10px rgba(255,46,207,.35); }
        .nbcp_msg { margin-top:10px;font-size:12px;text-align:center; }
        .nbcp_msg_error { color:rgba(255,120,120,.95); }
        .nbcp_msg_solved { color:rgba(120,255,190,.95); }
        .nbcp_hint_idle { margin-top:10px;padding:8px 12px;border-radius:8px;background:rgba(255,46,207,.07);border:1px solid rgba(255,46,207,.2);font-size:11px;color:rgba(255,200,245,.75);font-style:italic;text-align:center; }
        .nbcp_keypad { margin-top:14px;display:grid;grid-template-columns:repeat(3,1fr);gap:10px; }
        .nbcp_key { appearance:none;border-radius:14px;padding:14px 0;border:1px solid rgba(255,46,207,.65);background:linear-gradient(180deg,rgba(255,46,207,.10),rgba(0,0,0,.35));color:rgba(255,210,245,.95);font-weight:800;font-size:18px;letter-spacing:.08em;box-shadow:0 0 0 1px rgba(255,46,207,.18),0 10px 24px rgba(0,0,0,.45),0 0 18px rgba(255,46,207,.18);transition:transform .08s,box-shadow .18s,filter .18s;user-select:none; }
        .nbcp_key:hover { filter:brightness(1.1); }
        .nbcp_key:active { transform:translateY(2px) scale(.99); }
        .nbcp_key:disabled { opacity:.6;cursor:not-allowed; }
      `}</style>

      <div className="nbcp_wrap">
        <div className="nbcp_panel">
          <div className="nbcp_header">
            <div className="nbcp_hint">Affichage instable — segments défaillants</div>
            <div className="nbcp_hint" style={{ opacity: 0.7 }}>Entrer le code</div>
          </div>

          <div className="nbcp_displayRow">
            {broken.map((x, idx) => (
              <SevenSegDigit key={idx} digit={x.d} missingSegments={x.miss} size={92} flickerBroken={flickerBroken} />
            ))}
            <div className="nbcp_screenLine">De l'ombre naît la clé.</div>
          </div>

          {status !== "solved" && !solved && (
            <div className="nbcp_hint_idle">
              Quelque chose dans cette pièce porte le même symbole que le boîtier… 🐶
            </div>
          )}

          <div className="nbcp_inputBox">
            <div className="nbcp_code" aria-label="entered-code">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={cx("nbcp_dot", input[i] ? "nbcp_dotFilled" : "")}>{input[i] ? input[i] : " "}</div>
              ))}
            </div>
          </div>

          {status === "error" && <div className="nbcp_msg nbcp_msg_error">Code invalide. L'écran grésille…</div>}
          {(solved || status === "solved") && (
            <div>
              <div className="nbcp_msg nbcp_msg_solved">Accès autorisé. Le boîtier émet un bip net et un message apparaît :</div>
              <div className="nbcp_msg nbcp_msg_solved" style={{ margin: "auto", textAlign: "center", fontStyle: "italic" }}>
                L'icône de chien sur la boîte en verre n'est pas là par hasard !
              </div>
            </div>
          )}

          <NeonKeypad onPress={press} disabled={solved || status === "solved"} />
        </div>
      </div>
    </div>
  );
}